import { useStore } from '@tanstack/react-form';
import { useMemo } from 'react';

import type { PatientFormularyQueryKeyParams } from '@/entities/rx';

import type { RxFormApi } from './types';

interface FormularyFormSlice {
  dispensableDrugId: number | null;
  ndc: string | null;
  pharmacyId: number | null;
}

function shallowSliceEqual(a: FormularyFormSlice, b: FormularyFormSlice): boolean {
  return (
    a.dispensableDrugId === b.dispensableDrugId && a.ndc === b.ndc && a.pharmacyId === b.pharmacyId
  );
}

/**
 * Project the Rx wizard's draft values into the narrow shape the
 * ``/dosespot/patients/{user_id}/formulary`` endpoint consumes.
 *
 * Distinct from :func:`usePriorAuthBannerModel`: that one drives the
 * post-sign PA gate preview (and so needs every per-fill input), while
 * this one drives the formulary-status / alternatives panel that fires
 * the moment a medication is selected. We send only the medication
 * identifiers and (optionally) the chosen pharmacy — the backend
 * resolves the patient eligibility from the patient's primary coverage
 * for the channel.
 *
 * Subscribes to the form store via ``useStore`` so the parent step
 * re-renders the instant ``selectedMedication`` / ``selectedPharmacy``
 * change. Reading ``form.state.values`` directly does NOT subscribe —
 * the parent would only re-render coincidentally (e.g. on the next
 * keystroke in another field), making formulary look "delayed" after
 * picking a medication.
 *
 * Compound drafts have no single ``DispensableDrugId`` to look up, so
 * we return ``null`` for both ids and let the hook's ``enabled``
 * gating skip the fetch.
 *
 * ``patientEligibilityIdOverride`` lets the wizard pin the formulary
 * lookup to a specific coverage instead of letting the server pick the
 * first active coverage that covers the channel. Lives outside the
 * form draft (it's not part of the prescription itself) and is plumbed
 * through both the Details and Review steps so they stay in sync.
 */
export function usePatientFormularyParams(
  form: RxFormApi,
  patientEligibilityIdOverride: number | null = null
): PatientFormularyQueryKeyParams {
  const slice = useStore(
    form.store,
    (state) => {
      const values = state.values;
      const isCoded = values.kind === 'coded';
      return {
        dispensableDrugId: isCoded ? (values.selectedMedication?.DispensableDrugId ?? null) : null,
        ndc: isCoded ? (values.selectedMedication?.NDC ?? null) : null,
        pharmacyId: values.selectedPharmacy?.PharmacyId ?? null,
      };
    },
    { equal: shallowSliceEqual }
  );

  return useMemo(
    () => ({
      dispensableDrugId: slice.dispensableDrugId,
      ndc: slice.ndc,
      pharmacyId: slice.pharmacyId,
      patientEligibilityId: patientEligibilityIdOverride,
    }),
    [slice.dispensableDrugId, slice.ndc, slice.pharmacyId, patientEligibilityIdOverride]
  );
}
